// (c) 2018 Copyright Michael Martinez. All rights reserved
import Sodium from 'libsodium-wrappers'
import Phin from 'phin'
import Base64 from 'base64-js'
import QueryString from 'query-string'
import Yaml from 'js-yaml'

let protocol_version = '1'

export default class {
	constructor(name,config){
		this.apis = {}
		this.name = name
		this.url = config.url
		this.manifest = config.manifest
		this.token_loading = false
		this.pin = null
		this.sec = null // cache
		this.tok = null // cache
		this.redirect_callback = null
		this.getting_apis = false
		this.getting_apis_callbacks = []
	}

	unlockSecret(){
		if(localStorage.getItem(this.name+"Token")==null)
			return
		this.tok = localStorage.getItem(this.name+"Token")
		let sec = this.sec
		if(!sec){
			sec = this.sec = localStorage.getItem(this.name+"Secret")
			if(this.pin){
				const nnclen = Sodium.crypto_secretbox_NONCEBYTES * 2
				let seckey = this.pin
				while(seckey.length<Sodium.crypto_secretbox_KEYBYTES) // repeat to fit
					seckey += seckey
				let secnnc = sec.substring(0,nnclen)
				let enc = Base64.toByteArray(sec.substring(nnclen))
				sec = this.sec = Sodium.to_string(Sodium.crypto_secretbox_open_easy(enc,Sodium.from_hex(secnnc),Sodium.from_string(seckey.substring(0,Sodium.crypto_secretbox_KEYBYTES))))
			}
		}
		return sec
	}

	// returns a promise to resolve a tokenized parameter set
	insertTokens(unsigned){
		return new Promise((resolve,reject)=>{
			Sodium.ready.then(()=>{
				var files = {} // keep these out of the JSON stringy copy and signature
				for(var idx in unsigned){
					if(unsigned[idx] instanceof File){
						files[idx] = unsigned[idx]
						delete unsigned[idx]
					}
				}
				const data = (unsigned===undefined?{}:JSON.parse(JSON.stringify(unsigned)))
				data.dtver = protocol_version
				data.dt_ts = new Date().getTime()
				let sec = this.unlockSecret()
				let tok = this.tok
				data.dttok = tok
				let nnc = Sodium.randombytes_buf(Sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES)
				data.dtnnc = Base64.fromByteArray(nnc)

				let key = localStorage.getItem(this.name+"Token")+data.dtnnc
				let cks = []
				if(typeof(data.dt_ck)!=="undefined")
						cks = data.dt_ck.split(",")
				cks.push("dt_ts") // makes the validation stateless and tamper-proof
				data.dt_ck = cks.join(",")
				let box = {dttok:data.dttok} //token is public, but stored here to avoid same-secret
				for(let c of cks){
					if(typeof(data[c])==="object")
						box[c] = JSON.stringify(data[c])
					else
						box[c] = data[c]
					delete data[c]
				}
				delete data.dt_ck
				data.dtenc = Base64.fromByteArray(Sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
					Sodium.from_string(QueryString.stringify(box)),
					Sodium.from_string(protocol_version),null,nnc,
					Sodium.from_string(sec)
				))
				for(var idx in files){ // restore the files
					data[idx] = files[idx]
				}
				resolve(data)
			})
		})
	}

	// returns a promise to provide an API list
	getAPIs(){
		return new Promise((resolve,reject)=>{
			if(Object.keys(this.apis).length>0){
				resolve(this.apis)
			}else if(typeof(this.manifest)==="string"){
				// load the manifest asynchronously, but only once
				this.getting_apis_callbacks.push(resolve)
				if(!this.getting_apis){
					this.getting_apis = true
					let list = {}
					Phin({url:this.manifest},(e,r)=>{
						const apis = Yaml.safeLoad(r.body)
						for(let api in apis){
							let items = apis[api]
							for(let svc in items){
								let path = items[svc]
								list[svc] = {url:this.url,api:api,end:path}
							}
						}
						this.apis = list
						this.getting_apis = false
						this.getting_apis_callbacks.forEach((cb)=>{
							cb(list)
						})
					})
				}
			}else{ // use the manifest directly
				let list = {}
				for(let api in this.manifest){
					let items = this.manifest[api]
					for(let svc in items){
						let path = items[svc]
						list[svc] = {url:this.url,api:api,end:path}
					}
				}
				this.apis = list
				resolve(list)
			}
		})
	}

	signRequest(params,no_sign){
		return new Promise((resolve,reject)=>{
			if(no_sign)
				resolve(params)
			if(localStorage.getItem(this.name+"Token")==null){
				if(!this.token_loading){
					this.token_loading=true
					Sodium.ready.then(()=>{
						const box = Sodium.crypto_box_keypair()
						const search = QueryString.stringify({
							act:'relay_token',
							v:1,
							pub:Base64.fromByteArray(box.publicKey)
						})
						Phin({url:this.url+"?"+search,parse:"json"},(e,r)=>{
							let obj = r.body.obj
							let sec = Sodium.to_string(Sodium.crypto_box_seal_open(Base64.toByteArray(obj.relay_secret),box.publicKey,box.privateKey))
							//let sec = obj.relay_secret
							if(this.pin){
								let secnnc = Sodium.randombytes_buf(Sodium.crypto_secretbox_NONCEBYTES)
								let seckey = this.pin
								while(seckey.length<Sodium.crypto_secretbox_KEYBYTES) // repeat to fit
									seckey += seckey
								sec = Sodium.to_hex(secnnc) + Base64.fromByteArray(Sodium.crypto_secretbox_easy(sec,secnnc,Sodium.from_string(seckey.substring(0,Sodium.crypto_secretbox_KEYBYTES))))
							}
							localStorage.setItem(this.name+"Token",obj["relay_token"]),
							localStorage.setItem(this.name+"Secret",sec)
							this.token_loading=false
							this.insertTokens(params).then((data)=>{resolve(data)})
						})
					})
				}else{
					let pending = setInterval(()=>{
						if(!this.token_loading){
							clearInterval(pending)
							this.insertTokens(params).then((data)=>{resolve(data)})
						}
					},100)
				}
			}else{
				this.insertTokens(params).then((data)=>{resolve(data)})
			}
		})
	}

	getRedirectParams(){
		return JSON.parse(sessionStorage.getItem(this.name+"RedirectParams"))
	}

	transformResponse(e,r){
		let obj = null
		try{
			obj = JSON.parse(r.body)
		}catch(e){}
		if(r.statusCode==278){ // client-side redirect
			let parts = obj.location.split("?")
			if(parts.length>1){
				let params = QueryString.parse(parts[1])
				sessionStorage.setItem(this.name+"RedirectParams",JSON.stringify(params))
			}
			if(this.redirect_callback)
				this.redirect_callback(obj.location)
			else
				location.href = obj.location
			return null
		}
		return obj
	}

	clearTokens(){
		this.sec = null
		this.tok = null
		localStorage.removeItem(this.name+"Token"),
		localStorage.removeItem(this.name+"Secret"),
		sessionStorage.removeItem(this.name+"RedirectParams")
	}

	handleResponse(r){
		return new Promise((resolve,reject)=>{
			if(r.statusCode==442){
				let obj = JSON.parse(r.body)
				if(obj.err===-1){ // our relay token failed
					this.clearTokens()
					location.reload() // try again
				}
			}
			if(r.statusCode>=200 && r.statusCode<300)
				resolve(this.transformResponse(null,r))
			if(r.statusCode>400)
				reject(r)
		})
	}

	// returns a promise to retrieve a response
	request(config,end,params){
		return new Promise((resolve,reject)=>{
			if(typeof(config)==="string"){
				const method = config
				config = { method: method }
			}
			if(typeof(config.headers)==="undefined")
				config.headers = {}
			this.signRequest(params,config.no_sign).then((signed_params)=>{
				this.getAPIs().then((apis)=>{
					if(!apis[end]){
						console.error("Could not find API endpoint '"+end+"'. Check your API manifest.")
						if(failure!==undefined)
							failure()
						return
					}
					let api = apis[end]
					let ep = api.end.replace(/\{([^\}]+)\}/,function(match,key){
						let p = signed_params[key]
						delete signed_params[key]
						return p
					})
					if(typeof(config.url)==="undefined"){
						config.url = api.url+api.api+'/'+ep
						signed_params["end"] = ep
					}
					if(config.method=="GET"||config.method=="DELETE"){
						for(let k in signed_params) // handle nested params
							if(typeof(signed_params[k])==="object")
								signed_params[k] = JSON.stringify(signed_params[k])
						config.url += "?"+QueryString.stringify(signed_params)
					}else if(config.headers["Content-Type"]=="multipart/form-data"){
						const formData = new FormData();
						for(var idx in signed_params){
							formData.append(idx,signed_params[idx])
						}
						let xhr = new XMLHttpRequest()
						xhr.open(config.method,config.url)
						xhr.onreadystatechange = ()=>{
							if(xhr.readyState==4){ //we are done loading
								const r = {
									body: xhr.response,
									statusCode: xhr.status
								}
								this.handleResponse(r).then(resolve,reject)
							}
						}
						return xhr.send(formData)
					}else{
						config.data = signed_params
						if(typeof(config.headers["Content-Type"])==="undefined")
							config.headers["Content-Type"] = "application/json"
					}
					Phin(config,(e,r)=>{
						this.handleResponse(r).then(resolve,reject)
					})
				})
			})
		})
	}

	get(end,params,success,failure,always){
		return this.request("GET",end,params).then(success,failure).then(always)
	}
	post(end,params,success,failure,always){
		return this.request("POST",end,params).then(success,failure).then(always)
	}
	save(end,params,success,failure,always){
		return this.post(end,params,success,failure,always)
	}
	head(end,params,success,failure,always){
		return this.request("HEAD",end,params).then(success,failure).then(always)
	}
	delete(end,params,success,failure,always){
		return this.request("DELETE",end,params).then(success,failure).then(always)
	}
	remove(end,params,success,failure,always){
		return this.delete(end,params,success,failure,always)
	}
	patch(end,params,success,failure,always){
		return this.request("PATCH",end,params).then(success,failure).then(always)
	}

	/// use Content-Type: multipart/form-data
	upload(end,params,success,failure,always){
		return this.request({
			method:"POST",
			headers:{"Content-Type":"multipart/form-data"}
		},end,params).then(success,failure).then(always)
	}
}
